import {
  CollapsibleDrawer,
  type CollapsibleDrawerProps,
  type CollapsibleDrawerTriggerProps,
  type Key,
  Tab,
  TabList,
  TabPanel,
  type TabPanelProps,
  TabSize,
  TabVariant,
  Tabs,
} from '@vizit/atoms';
import { type Breakpoint, useBreakpoint } from '@vizit/hooks';
import { cx } from '@vizit/theme';
import * as React from 'react';

interface ProductAssetsDrawerProps extends Omit<CollapsibleDrawerProps, 'title' | 'side'> {
  /**
   * The class name of the collapsible drawer content.
   */
  contentClassName?: string;
  /**
   * Custom header content to replace the default header.
   * If provided, the default title and close button will not be rendered.
   */
  headerContent?: React.ReactNode;
}

interface ProductAssetsDrawerContentProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The key of the selected tab.
   */
  selectedKey?: Key | null;
  /**
   * The key of the default selected tab.
   */
  defaultSelectedKey?: Key;
  /**
   * Handler that is called when the selection changes.
   */
  onSelectionChange?: (key: Key | null) => void;
  /**
   * The key of the selected carousel sub-tab (imported or spark).
   */
  carouselSubTab?: 'imported' | 'spark' | null;
  /**
   * The key of the default selected carousel sub-tab.
   */
  defaultCarouselSubTab?: 'imported' | 'spark';
  /**
   * Handler that is called when the carousel sub-tab selection changes.
   */
  onCarouselSubTabChange?: (key: 'imported' | 'spark') => void;
  /**
   * The key of the selected hero sub-tab (imported or spark).
   * Only used when the hero panel renders nested sub-tabs.
   */
  heroSubTab?: 'imported' | 'spark' | null;
  /**
   * The key of the default selected hero sub-tab.
   */
  defaultHeroSubTab?: 'imported' | 'spark';
  /**
   * Handler that is called when the hero sub-tab selection changes.
   */
  onHeroSubTabChange?: (key: 'imported' | 'spark') => void;
  /**
   * Additional content to render alongside the carousel sub-tabs (e.g., action buttons).
   */
  carouselHeaderContent?: React.ReactNode;
  /**
   * Additional content to render in the hero tab header (e.g., action buttons).
   */
  heroHeaderContent?: React.ReactNode;
}

function ProductAssetsDrawerTrigger(props: CollapsibleDrawerTriggerProps) {
  return <CollapsibleDrawer.Trigger {...props} />;
}

function ProductAssetsDrawerHeroTabPanel(props: Omit<TabPanelProps, 'id'>) {
  const { children, className, ...other } = props;

  return (
    <TabPanel className={cx('px-10', className)} id="hero" {...other}>
      {children}
    </TabPanel>
  );
}

function ProductAssetsDrawerHeroImportedTabPanel(props: Omit<TabPanelProps, 'id'>) {
  const { children, className, ...other } = props;

  return (
    <TabPanel className={cx(className)} id="imported" {...other}>
      {children}
    </TabPanel>
  );
}

function ProductAssetsDrawerHeroSparkTabPanel(props: Omit<TabPanelProps, 'id'>) {
  const { children, className, ...other } = props;

  return (
    <TabPanel className={cx(className)} id="spark" {...other}>
      {children}
    </TabPanel>
  );
}

function ProductAssetsDrawerCarouselTabPanel(props: Omit<TabPanelProps, 'id'>) {
  const { children, className, ...other } = props;

  // This component now acts as a container for nested tabs
  return (
    <TabPanel className={cx('px-10', className)} id="carousel" {...other}>
      {children}
    </TabPanel>
  );
}

function ProductAssetsDrawerCarouselImportedTabPanel(props: Omit<TabPanelProps, 'id'>) {
  const { children, className, ...other } = props;

  return (
    <TabPanel className={cx(className)} id="imported" {...other}>
      {children}
    </TabPanel>
  );
}

function ProductAssetsDrawerCarouselSparkTabPanel(props: Omit<TabPanelProps, 'id'>) {
  const { children, className, ...other } = props;

  return (
    <TabPanel className={cx(className)} id="spark" {...other}>
      {children}
    </TabPanel>
  );
}

function ProductAssetsDrawerContent(props: ProductAssetsDrawerContentProps) {
  const {
    children,
    className,
    selectedKey,
    defaultSelectedKey,
    onSelectionChange,
    carouselSubTab,
    defaultCarouselSubTab,
    onCarouselSubTabChange,
    heroSubTab,
    defaultHeroSubTab,
    onHeroSubTabChange,
    carouselHeaderContent,
    heroHeaderContent,
    ...other
  } = props;

  const heroChild = React.Children.toArray(children).find((child) => {
    return React.isValidElement(child) && child.type === ProductAssetsDrawerHeroTabPanel;
  });

  const carouselChild = React.Children.toArray(children).find((child) => {
    return React.isValidElement(child) && child.type === ProductAssetsDrawerCarouselTabPanel;
  });

  const heroChildren = React.isValidElement(heroChild)
    ? React.Children.toArray((heroChild.props as { children?: React.ReactNode }).children)
    : [];

  const heroImportedChild = heroChildren.find((child) => {
    return React.isValidElement(child) && child.type === ProductAssetsDrawerHeroImportedTabPanel;
  });

  const heroSparkChild = heroChildren.find((child) => {
    return React.isValidElement(child) && child.type === ProductAssetsDrawerHeroSparkTabPanel;
  });

  // When the hero panel is wrapped in the new Imported/Spark sub-panels, render
  // the nested tabs. Otherwise fall back to rendering the hero panel children
  // inline (the existing single-panel behavior, used when the feature flag is
  // off).
  const hasHeroSubTabs = !!(heroImportedChild || heroSparkChild);

  const carouselChildren = React.isValidElement(carouselChild)
    ? React.Children.toArray((carouselChild.props as { children?: React.ReactNode }).children)
    : [];

  const importedChild = carouselChildren.find((child) => {
    return React.isValidElement(child) && child.type === ProductAssetsDrawerCarouselImportedTabPanel;
  });

  const sparkChild = carouselChildren.find((child) => {
    return React.isValidElement(child) && child.type === ProductAssetsDrawerCarouselSparkTabPanel;
  });

  const handleCarouselSubTabChange = React.useCallback(
    (key: Key | null) => {
      if (key && (key === 'imported' || key === 'spark')) {
        onCarouselSubTabChange?.(key);
      }
    },
    [onCarouselSubTabChange],
  );

  const handleHeroSubTabChange = React.useCallback(
    (key: Key | null) => {
      if (key && (key === 'imported' || key === 'spark')) {
        onHeroSubTabChange?.(key);
      }
    },
    [onHeroSubTabChange],
  );

  return (
    <div className={cx('flex flex-col gap-3 w-full', className)} {...other}>
      <Tabs
        variant={TabVariant.Underline}
        size={TabSize.Sm}
        className="w-full"
        selectedKey={selectedKey}
        defaultSelectedKey={defaultSelectedKey}
        onSelectionChange={onSelectionChange}
      >
        <div className="flex flex-col gap-6 pb-8 ml-12 max-w-[122px]">
          <TabList className="w-full">
            <Tab className="w-16 pl-0" id="hero" key="hero">
              Hero
            </Tab>
            <Tab className="w-32" id="carousel" key="carousel">
              Carousel
            </Tab>
          </TabList>
        </div>
        {/* Hero tab panel — nested Imported/Spark sub-tabs when provided, otherwise single-panel */}
        <TabPanel className="px-10 m-0" id="hero">
          {hasHeroSubTabs ? (
            <Tabs
              variant={TabVariant.Button}
              size={TabSize.Md}
              className="w-full"
              selectedKey={heroSubTab}
              defaultSelectedKey={defaultHeroSubTab}
              onSelectionChange={handleHeroSubTabChange}
            >
              <div className="flex items-center justify-between w-full">
                <TabList className="flex gap-1">
                  <Tab id="imported" key="imported" data-testid="product-drawer-hero-imported-tab">
                    Imported
                  </Tab>
                  <Tab id="spark" key="spark" data-testid="product-drawer-hero-spark-tab">
                    Spark
                  </Tab>
                </TabList>
                {heroHeaderContent}
              </div>
              {heroImportedChild}
              {heroSparkChild}
            </Tabs>
          ) : (
            <>
              {heroHeaderContent && (
                <div className="flex items-center justify-end w-full mb-4">{heroHeaderContent}</div>
              )}
              {React.isValidElement(heroChild) ? (heroChild.props as { children?: React.ReactNode }).children : null}
            </>
          )}
        </TabPanel>
        {/* Carousel tab panel with nested tabs */}
        <TabPanel className="px-10 m-0" id="carousel">
          <Tabs
            variant={TabVariant.Button}
            size={TabSize.Md}
            className="w-full"
            selectedKey={carouselSubTab}
            defaultSelectedKey={defaultCarouselSubTab}
            onSelectionChange={handleCarouselSubTabChange}
          >
            <div className="flex items-center justify-between w-full">
              <TabList className="flex gap-1">
                <Tab id="imported" key="imported" data-testid="product-drawer-carousel-imported-tab">
                  Imported
                </Tab>
                <Tab id="spark" key="spark" data-testid="product-drawer-carousel-spark-tab">
                  Spark
                </Tab>
              </TabList>
              {carouselHeaderContent}
            </div>
            {importedChild}
            {sparkChild}
          </Tabs>
        </TabPanel>
      </Tabs>
    </div>
  );
}

/**
 * The fixed widths this drawer had before it became resizable: `524px` at >= xl
 * and `366px` below it (from `CollapsibleDrawer`'s `contentWidth="custom"`
 * grid columns). They serve as both the opening width and the floor — the
 * drawer only ever expands from the layout it used to have, never below it.
 */
const BASELINE_WIDTH = {
  XL_UP: 524,
  BELOW_XL: 366,
} as const;

/** Breakpoints at or above Tailwind's `xl` (1280px), where the drawer was 524px. */
const XL_UP_BREAKPOINTS: ReadonlyArray<Breakpoint | 'base'> = ['xl', '2xl', '3xl', '4xl'];

function ProductAssetsDrawer(props: ProductAssetsDrawerProps) {
  const { children: childrenProp, contentClassName, headerContent, ...other } = props;

  // The pre-resize layout was breakpoint-dependent, so the floor is too —
  // pinning it to the xl width would make the drawer wider than it used to be
  // on smaller screens.
  const breakpoint = useBreakpoint();
  const baselineWidth = XL_UP_BREAKPOINTS.includes(breakpoint) ? BASELINE_WIDTH.XL_UP : BASELINE_WIDTH.BELOW_XL;

  const children = React.Children.toArray(childrenProp).filter((child) => {
    return React.isValidElement(child) && child.type !== ProductAssetsDrawerContent;
  });

  const content = React.Children.toArray(childrenProp).find((child) => {
    return React.isValidElement(child) && child.type === ProductAssetsDrawerContent;
  });

  return (
    <CollapsibleDrawer
      title="Assets"
      side="right"
      contentWidth="custom"
      isResizable
      defaultWidth={baselineWidth}
      minWidth={baselineWidth}
      // No `resizeStorageKey`: the width is deliberately not persisted, so the
      // panel opens at its baseline every time the page is mounted.
      resizeHandleLabel="Resize assets panel"
      {...other}
    >
      {children}
      <CollapsibleDrawer.Content className={contentClassName} headerContent={headerContent}>
        {content}
      </CollapsibleDrawer.Content>
    </CollapsibleDrawer>
  );
}

export {
  ProductAssetsDrawer,
  ProductAssetsDrawerContent,
  ProductAssetsDrawerHeroTabPanel,
  ProductAssetsDrawerHeroImportedTabPanel,
  ProductAssetsDrawerHeroSparkTabPanel,
  ProductAssetsDrawerCarouselTabPanel,
  ProductAssetsDrawerCarouselImportedTabPanel,
  ProductAssetsDrawerCarouselSparkTabPanel,
  ProductAssetsDrawerTrigger,
};
export type { ProductAssetsDrawerProps, ProductAssetsDrawerContentProps };
